import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { FileDown, Eye, SearchX, CheckCircle2, Clock, Truck, PackageCheck, ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import api, { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const PER_PAGE = 25;

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "dd MMM yyyy, HH:mm");
  } catch {
    return iso;
  }
};

const statusMeta = {
  pending: { label: "Pending", cls: "bg-[#c47b28]/15 text-[#8a4c0d] border-[#c47b28]/40", Icon: Clock },
  approved: {
    label: "Approved",
    cls: "bg-[#1d3a14]/15 text-[#1d3a14] border-[#1d3a14]/40",
    Icon: CheckCircle2,
  },
};

const dispatchMeta = {
  pending_dispatch: { label: "Pending for Dispatch", cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: Truck },
  despatched: { label: "Despatched", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: PackageCheck },
};

export default function OrdersList() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE };
      if (statusFilter !== "all") params.status = statusFilter;
      if (query.trim()) params.q = query.trim();
      const { data } = await api.get("/orders", { params });
      setOrders(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, query]);

  // Debounce search + auto-reload on filter changes
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const exportUrl = `${API}/orders/export.xlsx`;

  const handleExport = async () => {
    try {
      const res = await api.get("/orders/export.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rmj_orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const canExport = user?.role === "coordinator" || user?.role === "billing";
  const pageTitle = user?.role === "salesperson" ? "My Orders" : "All Orders";

  return (
    <div className="grain ui-sans pb-16" data-testid="orders-list-page">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="brand-serif text-3xl sm:text-4xl font-bold text-[#1d3a14]">
              {pageTitle}
            </h1>
            <p className="text-sm text-[#5b6b4a] mt-1">
              {total} order{total !== 1 ? "s" : ""}
            </p>
          </div>
          {canExport && (
            <Button
              onClick={handleExport}
              data-testid="export-excel-button"
              className="bg-[#1d3a14] hover:bg-[#264a1a] text-[#faf6ea]"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          )}
        </div>

        <div className="flex gap-3 flex-wrap bg-white/80 border border-[#d6cdb4] rounded-xl p-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Search order #, customer, city, salesperson, SO#, invoice…"
              value={query}
              onChange={(e) => {
                setPage(1);
                setQuery(e.target.value);
              }}
              data-testid="orders-search-input"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setPage(1);
              setStatusFilter(v);
            }}
          >
            <SelectTrigger className="w-48" data-testid="orders-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="status-all">All statuses</SelectItem>
              <SelectItem value="pending" data-testid="status-pending">Pending</SelectItem>
              <SelectItem value="approved" data-testid="status-approved">Approved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-center text-[#5b6b4a] py-12" data-testid="orders-loading">
            Loading…
          </p>
        ) : orders.length === 0 ? (
          <div
            className="bg-white/80 border border-dashed border-[#c4b991] rounded-xl p-12 text-center"
            data-testid="orders-empty"
          >
            <SearchX className="h-10 w-10 text-[#5b6b4a] mx-auto mb-3" />
            <p className="text-[#5b6b4a]">No orders match the current filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto bg-white/85 border border-[#d6cdb4] rounded-xl shadow-sm">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead className="bg-[#1d3a14] text-[#faf6ea]">
                  <tr>
                    <th className="text-left px-3 py-2">Order #</th>
                    <th className="text-left px-3 py-2">Date</th>
                    {user?.role !== "salesperson" && (
                      <th className="text-left px-3 py-2">Salesperson</th>
                    )}
                    <th className="text-left px-3 py-2">Customer</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Dispatch</th>
                    <th className="text-left px-3 py-2">SO #</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o, i) => {
                    const sMeta = statusMeta[o.status] || {
                      label: o.status,
                      cls: "bg-gray-100 text-gray-700 border-gray-300",
                      Icon: Clock,
                    };
                    const dMeta = dispatchMeta[o.dispatch_status];
                    return (
                      <tr
                        key={o.order_id}
                        className={cn(
                          "border-t border-[#efe7cf]",
                          i % 2 === 0 ? "bg-[#fbf8ec]" : "bg-white"
                        )}
                        data-testid={`order-row-${o.order_id}`}
                      >
                        <td className="px-3 py-2 font-mono text-[#1d3a14] font-semibold">
                          {o.order_no}
                        </td>
                        <td className="px-3 py-2 text-[#5b6b4a]">
                          {formatDate(o.created_at)}
                        </td>
                        {user?.role !== "salesperson" && (
                          <td className="px-3 py-2">{o.salesperson_name}</td>
                        )}
                        <td className="px-3 py-2">
                          <div>{o.customer?.name}</div>
                          {o.customer?.city && (
                            <div className="text-xs text-[#5b6b4a]">{o.customer.city}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {inr(o.subtotal)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border",
                              sMeta.cls
                            )}
                            data-testid={`order-status-${o.order_id}`}
                          >
                            <sMeta.Icon className="h-3 w-3" />
                            {sMeta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {dMeta ? (
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border",
                                dMeta.cls
                              )}
                            >
                              <dMeta.Icon className="h-3 w-3" />
                              {dMeta.label}
                            </span>
                          ) : (
                            <span className="text-[#9aa391] text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[#5b6b4a] text-xs">
                          {o.sale_order_number || "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Link
                            to={`/orders/${o.order_id}`}
                            data-testid={`view-order-${o.order_id}`}
                          >
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#c4b991] hover:bg-[#efe7cf]"
                            >
                              <Eye className="h-3.5 w-3.5 mr-1" />
                              View
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 text-sm text-[#5b6b4a]">
              <div data-testid="orders-pagination-info">
                Showing {(page - 1) * PER_PAGE + 1}–
                {Math.min(page * PER_PAGE, total)} of {total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="orders-prev-page"
                  className="border-[#c4b991] hover:bg-[#efe7cf]"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </Button>
                <span data-testid="orders-page-indicator">
                  Page {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="orders-next-page"
                  className="border-[#c4b991] hover:bg-[#efe7cf]"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </main>
      <a href={exportUrl} hidden aria-hidden="true">x</a>
    </div>
  );
}
